import { ColDef } from 'ag-grid-community';
import { FloraColDef } from './ag-grid-mobile.columns';
import { ActionsCellRendererComponent } from './cell-renderers/render-components/actions/actions.component';
import { TextCellRendererComponent } from './cell-renderers/render-components/text/text.component';
import { LinkCellRendererComponent } from './cell-renderers/render-components/link/link.component';
import { BooleanEditorComponent } from './cell-editors/boolean/boolean.component';
import { CalendarEditorComponent } from './cell-editors/calendar/calendar.component';
import { AgGridToolsService } from 'src/app/common/ag-grid-table/services/ag-grid-tools.service';
import { MultiselectEditorComponent } from './cell-editors/multiselect/multiselect.component';

interface CustomColDef extends ColDef {
  cellClass?: string;
}

type IdLink = {
  isLink: boolean;
  cellRendererParams?: any
};

export function getDefaultTableColumns(entity: string, agGridToolsService: AgGridToolsService, tableParams: any = null, idLink: IdLink | null = null): ColDef[] {
  const fieldId: FloraColDef = {
    headerName: 'ID',
    field: 'id',
    mobileCritical: false,
    width: 125,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    sortable: true,
    initialSort: 'desc',
    sortIndex: 0,
    initialSortIndex: 0,
    sort: 'desc',
    sortingOrder: ['asc','desc'],
    unSortIcon: false,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: [
        'equals',
        'notEqual',
        'lessThan',
        'lessThanOrEqual',
        'greaterThan',
        'greaterThanOrEqual',
        'inRange'
      ]
    },
    resizable: true,
    floatingFilter: true,
    suppressSizeToFit: true,
  };

  const fieldName: FloraColDef = {
    headerName: 'Name',
    field: 'name',
    mobileCritical: true,
    cellRendererParams: {
      entity: entity
    },
    cellRenderer: LinkCellRendererComponent,
    sortable: true,
    unSortIcon: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    cellEditor: 'agTextCellEditor',
    type: 'editableColumn',
  };

  if (idLink?.isLink) {
    fieldId.cellRendererParams = {
      entity: entity,
      ...idLink.cellRendererParams
    };
    fieldId.cellRenderer = LinkCellRendererComponent;

    delete fieldName.cellRenderer;
  }

  const actionsColumn: FloraColDef = {
    headerName: 'Actions',
    field: 'actions',
    mobileCritical: true,
    cellRenderer: ActionsCellRendererComponent,
    pinned: 'right', // Pin the column to the right
    width: 120,
    minWidth: 80,
    resizable: true,
    cellRendererParams: {
      enableDuplicateFeature: tableParams?.enableDuplicateFeature || false,
      isDynamicEntity: tableParams?.isDynamicEntity || false,
    }
  }

  const columns: FloraColDef[] = [
    fieldId,
    fieldName,
    {
      headerName: 'Enabled',
      field: 'enabled',
      mobileCritical: true,
      cellRenderer: TextCellRendererComponent,
      cellRendererParams: {
        onLabel: 'Enabled',
        offLabel: 'Not Enabled',
        isBoolean: true
      },
      sortable: true,
      unSortIcon: true,
      resizable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
      },
      floatingFilter: true,
      cellEditor: BooleanEditorComponent,
      type: 'editableColumn',
    },
    {
      headerName: 'Start',
      field: 'start',
      sortable: true,
      unSortIcon: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear']
      },
      floatingFilter: true,
      suppressSizeToFit: true,
      width: 300,
      cellRenderer: TextCellRendererComponent,
      cellRendererParams: {
        isDate: true,
      },
      valueGetter: agGridToolsService.dateValueGetter('start'),
      resizable: true,
      cellEditor: CalendarEditorComponent,
      type: 'editableColumn'
    },
    {
      headerName: 'End',
      field: 'end',
      sortable: true,
      unSortIcon: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear']
      },
      floatingFilter: true,
      suppressSizeToFit: true,
      cellRenderer: TextCellRendererComponent,
      cellRendererParams: {
        isDate: true,
        timePill: true
      },
      width: 300,
      valueGetter: agGridToolsService.dateValueGetter('end'),
      resizable: true,

      type: 'editableColumn',
      cellEditor: CalendarEditorComponent,
    },
    {
      headerName: 'Environment(s)',
      field: 'env',
      cellRendererParams: {
        entity: entity,
        isEnv: true
      },
      cellRenderer: TextCellRendererComponent,
      sortable: true,
      unSortIcon: true,
      resizable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
          values: ['dev', 'qa', 'prod', 'test'],
      },
      floatingFilter: true,
      cellEditor: MultiselectEditorComponent,
      type: 'editableColumn',
      cellEditorPopup: true,
      cellEditorParams: {
        optionValue: "_id",
        optionLabel: "name",
        valueObjects: true,
        isFlatList: true
      },
    },
    {
      headerName: 'Bundle Asset',
      field: 'bundleAsset',
      cellRenderer: TextCellRendererComponent,
      cellRendererParams: {
        onLabel: 'Bundle',
        offLabel: 'Not Bundle',
        isBoolean: true
      },
      sortable: true,
      unSortIcon: true,
      resizable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        valueFormatter: (params: any) => params.value ? 'Bundle' : 'Not Bundle'
      },
      floatingFilter: true,

      cellEditor: BooleanEditorComponent,
      type: 'editableColumn',
      cellEditorPopupPosition: 'under'
    },
    {
      headerName: 'Bundle Image',
      field: 'bundleImage',
      cellRenderer: TextCellRendererComponent,
      cellRendererParams: {
        onLabel: 'Bundle',
        offLabel: 'Not Bundle',
        isBoolean: true
      },
      sortable: true,
      unSortIcon: true,
      resizable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        valueFormatter: (params: any) => params.value ? 'Bundle' : 'Not Bundle'
      },
      floatingFilter: true,

      cellEditor: BooleanEditorComponent,
      type: 'editableColumn',
      cellEditorPopupPosition: 'under'
    },
  ];

  if (!tableParams?.noActionsColumn) { 
    columns.unshift(actionsColumn);
  }

  return columns;
}

export function getDefaultTableColumnsForDynamicEntity(entity: string, agGridToolsService: AgGridToolsService, tableParams: any = null, idLink: IdLink | null = null): ColDef[] {
  const fieldId: FloraColDef = {
    headerName: 'ID',
    field: 'id',
    mobileCritical: false,
    width: 125,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    sortable: true,
    initialSort: 'desc',
    sortIndex: 0,
    initialSortIndex: 0,
    sort: 'desc',
    sortingOrder: ['asc','desc'],
    unSortIcon: false,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: [
        'equals',
        'notEqual',
        'lessThan',
        'lessThanOrEqual',
        'greaterThan',
        'greaterThanOrEqual',
        'inRange'
      ]
    },
    resizable: true,
    floatingFilter: true,
    suppressSizeToFit: true,
  };

  const fieldName: FloraColDef = {
    headerName: 'Name',
    field: 'name',
    mobileCritical: true,
    cellRendererParams: {
      entity: entity
    },
    cellRenderer: LinkCellRendererComponent,
    sortable: true,
    unSortIcon: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    cellEditor: 'agTextCellEditor',
    type: 'editableColumn',
  };

  if (idLink?.isLink) {
    fieldId.cellRendererParams = {
      entity: entity,
      ...idLink.cellRendererParams
    };
    fieldId.cellRenderer = LinkCellRendererComponent;

    delete fieldName.cellRenderer;
  }

  const fields: FloraColDef[] = [
    {
      headerName: 'Actions',
      field: 'actions',
      mobileCritical: true,
      cellRenderer: ActionsCellRendererComponent,
      pinned: 'right', // Pin the column to the right
      width: 120,
      minWidth: 80,
      resizable: true,
      cellRendererParams: {
        enableDuplicateFeature: tableParams?.enableDuplicateFeature || false,
        isDynamicEntity: tableParams?.isDynamicEntity || false,
      }
    },
    fieldId,
  ];

  if (tableParams.hasName) {
    fields.push(fieldName);
  }

  fields.push({
    headerName: 'Enabled',
    field: 'enabled',
    mobileCritical: true,
    cellRenderer: TextCellRendererComponent,
    cellRendererParams: {
      onLabel: 'Enabled',
      offLabel: 'Not Enabled',
      isBoolean: true
    },
    sortable: true,
    unSortIcon: true,
    resizable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [true, false],
      valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
    },
    floatingFilter: true,
    cellEditor: BooleanEditorComponent,
    type: 'editableColumn',
  });

  if (tableParams.hasStart) {
    fields.push({
      headerName: 'Start',
      field: 'start',
      sortable: true,
      unSortIcon: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear']
      },
      floatingFilter: true,
      suppressSizeToFit: true,
      width: 300,
      cellRenderer: TextCellRendererComponent,
      cellRendererParams: {
        isDate: true,
      },
      valueGetter: agGridToolsService.dateValueGetter('start'),
      resizable: true,
      cellEditor: CalendarEditorComponent,
      type: 'editableColumn',
    });
  }

  if (tableParams.hasEnd) {
    fields.push({
      headerName: 'End',
      field: 'end',
      sortable: true,
      unSortIcon: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear']
      },
      floatingFilter: true,
      suppressSizeToFit: true,
      cellRenderer: TextCellRendererComponent,
      cellRendererParams: {
        isDate: true,
        timePill: true
      },
      width: 300,
      valueGetter: agGridToolsService.dateValueGetter('end'),
      resizable: true,

      type: 'editableColumn',
      cellEditor: CalendarEditorComponent,
    });
  }

  fields.push({
    headerName: 'Environment(s)',
    field: 'env',
    cellRendererParams: {
      entity: entity,
      isEnv: true
    },
    cellRenderer: TextCellRendererComponent,
    sortable: true,
    unSortIcon: true,
    resizable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
        values: ['dev', 'qa', 'prod', 'test'],
    },
    floatingFilter: true,
    cellEditor: MultiselectEditorComponent,
    type: 'editableColumn',
    cellEditorPopup: true,
    cellEditorParams: {
      optionValue: "_id",
      optionLabel: "name",
      valueObjects: true,
      isFlatList: true
    },
  });

  return fields;
}
